import { syncState } from '@legendapp/state';
import { observer, useMount } from '@legendapp/state/react';
import React, { useCallback, useMemo, useEffect } from 'react';
import { StockAvailabilityProvider } from '../../contexts';
import {
    useBatchProductStockAvailability,
    useCart,
    useCartActionsV3,
    useCartItemReference,
    useExpandableCartItems,
} from '../../hooks';
import { type CartItemReference } from '../../models';
import { cartItemReferenceStoreAPI } from '../../stores/cart-item-reference-store.public-api';
import { cartStoreStateAPI } from '../../stores/cart-store.public-api';
import type { CartItemInterface } from '../../types/front.graphql';
import { CartItemGroupRenderer } from './cart-item-group-renderer';
import type { CartCounterButtonProps, CartItemsProps } from './cart-items.types';
import {
    FlatList,
    CartEmpty,
    type ListRenderItemInfo,
    cn,
    CartItemGroupVariantEnum,
    withDialog,
} from '@kidslabs/mario-nextjs-react-ui-core';
import { ExpandedButton } from '../expanded-button/expanded-button';
import styles from './cart-items.module.css';
import { useTranslation } from 'next-i18next';

/**
 * CartItems - Cart items list component matching mobile API
 *
 * Uses FlatList from UI Core (same API as React Native FlatList)
 *
 * @see mobile-ecommerce/applications/module-quote/src/components/cart-items/cart-items.tsx
 */
const CartItemsContent: React.FC<CartItemsProps> = observer(
    ({
        className,
        counterButton = true,
        cartItemWrapperStyle,
        cartItemGroupStyle,
        showOnlySelected = false,
        CartItemGroupComponent,
        onPressItem,
        onOutOfStockPress,
        ListHeaderComponent,
        ListFooterComponent,
        AfterListComponent,
        variantCounterButton,
        variantCartItemV2,
        variantSizePriceBox,
        variantCartItemGroup,
        expanded = false,
        isSwipeable,
        hasInvalidLogicRule,
        onValidateLogicsBeforeSelect,
        showDialog,
        hideDialog,
        setDialogOptions,
    }) => {
        const { t } = useTranslation('quote');

        const { isCartLoading, cartId } = useCart();

        const {
            handleRemoveItemsInCart,
            getValidEnrichedCartItemReferences,
            setCartItemReferenceSelected,
            handleDecreaseQuantity,
            handleIncreaseQuantity,
            handleChangeQuantity,
            getAllLogicActions,
        } = useCartActionsV3();

        const { references$ } = useCartItemReference();
        const cartItemReferencesData = references$().get();
        const cartItemReferences = useMemo(
            () => (cartItemReferencesData || []).filter(ref => ref.cartItemReferenceId && !!ref?.cartItem),
            [cartItemReferencesData]
        );

        const enrichedCartItemReferences = useMemo(
            () => getValidEnrichedCartItemReferences({ showOnlySelected, cartItemReferences }),
            [cartItemReferences, getValidEnrichedCartItemReferences, showOnlySelected]
        );

        // ✅ USE EXPANDABLE CART ITEMS: Matches mobile pattern for expand/collapse functionality
        const { enrichedCartItemsRendered, isCartItemsExpandable, isExpanded, toggleExpanded } = useExpandableCartItems(
            enrichedCartItemReferences,
            showOnlySelected,
            expanded
        );

        // ✅ BATCH STOCK QUERY FOR ENTIRE CART: Query all items at once (1 request instead of N)
        const allCartItems = useMemo<CartItemInterface[]>(() => {
            const items: CartItemInterface[] = [];
            enrichedCartItemReferences.forEach(ref => {
                if (ref.cartItem) {
                    items.push(ref.cartItem as CartItemInterface);
                }
            });
            return items;
        }, [enrichedCartItemReferences]);

        const logicActions = useMemo(() => {
            return getAllLogicActions({ cartItemReferences });
        }, [cartItemReferences, getAllLogicActions]);

        const stockAvailabilityMap = useBatchProductStockAvailability(allCartItems, logicActions);

        // ✅ SYNC STOCK AVAILABILITY TO STORE: Matches mobile pattern (line 112)
        useEffect(() => {
            if (stockAvailabilityMap && Object.keys(stockAvailabilityMap).length > 0) {
                cartStoreStateAPI.setAvailableItems(stockAvailabilityMap);
            }
        }, [stockAvailabilityMap]);

        const handleRightActionPressed = useCallback(
            (cartItemReference: CartItemReference) => {
                const currentCartItem = cartItemReference?.cartItem as CartItemInterface | undefined;
                if (!currentCartItem) return;
                handleRemoveItemsInCart([
                    {
                        item: currentCartItem,
                        value: currentCartItem.quantity ?? 1,
                        cartItemReference: cartItemReference,
                    },
                ]);
            },
            [handleRemoveItemsInCart]
        );

        const handleShowDialog = useCallback(
            (message: string, onConfirm?: () => void, onDismiss?: () => void) => {
                setDialogOptions({
                    title: t('text_notification', 'Thông báo'),
                    content: message,
                    acceptLabel: t('text_agree', 'Đồng ý'),
                    dismissLabel: t('text_cancel', 'Quay lại'),
                    loading: false,
                    onAccept: () => {
                        onConfirm?.();
                        hideDialog();
                    },
                    onDismiss: () => {
                        onDismiss?.();
                        hideDialog();
                    },
                });
                showDialog();
            },
            [setDialogOptions, showDialog, hideDialog, t]
        );

        const handleSelected = useCallback(
            ({ cartItemReference: selectedCartItemReference }: { cartItemReference: CartItemReference }) => {
                console.log('Selecting cart item reference:', selectedCartItemReference.cartItemReferenceId);
                setCartItemReferenceSelected(selectedCartItemReference.cartItemReferenceId, true);
            },
            [setCartItemReferenceSelected]
        );

        const handleUnSelected = useCallback(
            ({ cartItemReference: selectedCartItemReference }: { cartItemReference: CartItemReference }) => {
                console.log('Unselecting cart item reference:', selectedCartItemReference.cartItemReferenceId);
                setCartItemReferenceSelected(selectedCartItemReference.cartItemReferenceId, false);
            },
            [setCartItemReferenceSelected]
        );

        const handleBeforeSelect = useCallback(
            ({ cartItemReference: selectedCartItemReference }: { cartItemReference: CartItemReference }) => {
                const hasCartLogics = hasInvalidLogicRule?.() ?? false;

                if (!hasCartLogics || selectedCartItemReference?.logics?.length === 0) {
                    handleSelected({ cartItemReference: selectedCartItemReference });
                    return;
                }

                handleShowDialog(
                    t(
                        'text_selected_gift_cart',
                        'Sản phẩm ba mẹ đang chọn đã có quà tặng. Nếu tiếp tục, quà tặng đã chọn trước đó sẽ bị thay thế'
                    ),
                    () => {
                        onValidateLogicsBeforeSelect?.();
                        handleSelected({ cartItemReference: selectedCartItemReference });
                    },
                    () => {
                        handleUnSelected({ cartItemReference: selectedCartItemReference });
                    }
                );
            },
            [onValidateLogicsBeforeSelect, handleSelected, handleShowDialog, handleUnSelected, t, hasInvalidLogicRule]
        );

        // ✅ CART COUNTER BUTTON PROPS: Bundled props matching mobile pattern
        const cartCounterButtonProps = useMemo<CartCounterButtonProps>(
            () => ({
                handleIncrease: handleIncreaseQuantity,
                handleDecrease: handleDecreaseQuantity,
                emitQuantityChange: handleChangeQuantity,
                isLoading: isCartLoading,
                isDisableDecrease: false,
                isDisableIncreaseProductDetail: false,
                disabledInput: false,
                disabled: false,
            }),
            [handleIncreaseQuantity, handleDecreaseQuantity, handleChangeQuantity, isCartLoading]
        );

        // ✅ HANDLE ITEM PRESSED: Matches mobile signature ({item}: {item: CartItemInterface}) => void
        const handleItemPressed = useCallback(
            ({ item }: { item: CartItemInterface }) => {
                const sku = item?.product?.sku;

                if (sku) {
                    onPressItem?.(sku);
                }
            },
            [onPressItem]
        );

        // ✅ HANDLE OUT OF STOCK PRESS: Matches mobile signature (cartItem: CartItemInterface) => void
        const handleOutOfStockPress = useCallback(
            (cartItem: CartItemInterface) => {
                const closestCategory = cartItem?.product?.sku;
                if (closestCategory) {
                    onOutOfStockPress?.(closestCategory);
                }
            },
            [onOutOfStockPress]
        );

        const keyExtractorCartItemReference = useCallback((item: CartItemReference) => item.cartItemReferenceId, []);

        // ✅ RENDER EXPANDED BUTTON: Matches mobile pattern
        const renderExpandedButton = useCallback(() => {
            return isCartItemsExpandable ? (
                <ExpandedButton
                    text={isExpanded ? 'Thu gọn' : 'Hiện đầy đủ'}
                    toggleExpanded={toggleExpanded}
                    isExpanded={isExpanded}
                    className={styles.expandedButton}
                />
            ) : null;
        }, [isCartItemsExpandable, isExpanded, toggleExpanded]);

        // ✅ RENDER COMPONENT OR ELEMENT: Helper function matching mobile pattern
        const renderComponentOrElement = useCallback(
            (component: React.ReactNode | (() => React.ReactNode) | null | undefined) => {
                if (!component) {
                    return null;
                }
                if (React.isValidElement(component)) {
                    return component;
                }
                if (typeof component === 'function') {
                    const Component = component as React.ComponentType;
                    return <Component />;
                }
                return component;
            },
            []
        );

        // Render cart item reference - matches mobile renderItem signature
        const renderCartItemReference = useCallback(
            ({ item, index }: ListRenderItemInfo<CartItemReference>) => {
                const cartItemGroupClassName = index === enrichedCartItemsRendered.length - 1 ? null : styles.divider;
                return (
                    <CartItemGroupRenderer
                        variantCounterButton={variantCounterButton}
                        variantCartItemV2={variantCartItemV2}
                        variantSizePriceBox={variantSizePriceBox}
                        variantCartItemGroup={variantCartItemGroup}
                        expanded={expanded}
                        cartItemReference={item}
                        cartId={cartId}
                        showOnlySelected={showOnlySelected}
                        isSwipeable={isSwipeable}
                        cartItemWrapperStyle={cartItemWrapperStyle}
                        cartItemGroupStyle={cn(cartItemGroupClassName, cartItemGroupStyle)}
                        counterButton={counterButton}
                        isCartLoading={isCartLoading}
                        CartItemGroupComponent={CartItemGroupComponent}
                        cartCounterButtonProps={cartCounterButtonProps}
                        onItemPressed={handleItemPressed}
                        onSelected={handleBeforeSelect}
                        onUnSelected={handleUnSelected}
                        onRightActionPressed={handleRightActionPressed}
                        onOutOfStockPress={handleOutOfStockPress}
                    />
                );
            },
            [
                cartId,
                enrichedCartItemsRendered,
                showOnlySelected,
                cartItemWrapperStyle,
                cartItemGroupStyle,
                counterButton,
                isCartLoading,
                CartItemGroupComponent,
                cartCounterButtonProps,
                handleItemPressed,
                handleBeforeSelect,
                handleUnSelected,
                handleRightActionPressed,
                handleOutOfStockPress,
                variantCounterButton,
                variantCartItemV2,
                variantSizePriceBox,
                variantCartItemGroup,
                expanded,
                isSwipeable,
            ]
        );

        // Render footer - combines ExpandedButton, ListFooterComponent and AfterListComponent like mobile
        const renderFooter = useCallback(() => {
            const footer = renderComponentOrElement(ListFooterComponent);
            const after = renderComponentOrElement(AfterListComponent);
            const expandedBtn = renderExpandedButton();

            if (!expandedBtn && !footer && !after) return null;

            return (
                <div className={styles.listFooter}>
                    {expandedBtn}
                    {footer}
                    {after}
                </div>
            );
        }, [renderExpandedButton, renderComponentOrElement, ListFooterComponent, AfterListComponent]);

        // Render header - matches mobile pattern
        const renderHeader = useCallback(() => {
            if (!ListHeaderComponent) return null;

            return renderComponentOrElement(ListHeaderComponent);
        }, [ListHeaderComponent, renderComponentOrElement]);

        useMount(() => {
            syncState(cartItemReferenceStoreAPI.state.getStoreObservable())
                .sync()
                .catch(error => {
                    console.error('Error syncing cart item reference store:', error);
                });
        });

        const contentContainerClass = useMemo(() => {
            if (expanded) return undefined;

            if (variantCartItemGroup === CartItemGroupVariantEnum.FULL || !isSwipeable) {
                return cn(styles.listContent, styles.listContentBorder);
            }

            return styles.listContent;
        }, [expanded, variantCartItemGroup, isSwipeable]);

        return (
            <StockAvailabilityProvider value={stockAvailabilityMap}>
                <div className={cn(styles.container, className)}>
                    <FlatList
                        data={enrichedCartItemsRendered}
                        renderItem={renderCartItemReference}
                        contentContainerClassName={contentContainerClass}
                        keyExtractor={keyExtractorCartItemReference}
                        showsVerticalScrollIndicator={false}
                        ListEmptyComponent={<CartEmpty />}
                        ListFooterComponent={renderFooter}
                        ListHeaderComponent={renderHeader}
                    />
                </div>
            </StockAvailabilityProvider>
        );
    }
);

export const CartItems = withDialog(CartItemsContent);
